import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  api,
  clearTokens,
  getRefreshToken,
  onUnauthenticated,
  resumeSession,
  storeTokens,
} from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const signOutLocally = useCallback(() => {
    clearTokens();
    setUser(null);
  }, []);

  useEffect(() => {
    onUnauthenticated(signOutLocally);
  }, [signOutLocally]);

  // A stored refresh token survives a reload; exchange it before rendering routes so
  // a signed-in user is not bounced back to the login screen.
  //
  // Through resumeSession rather than a refresh call of its own. Rotation revokes the
  // token it spends, so two of them racing means one is refused — and this effect used
  // to be the second path, outside the guard that stops that happening. A reload while
  // another tab was refreshing was enough.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const auth = await resumeSession();
        if (cancelled) return;
        if (auth) setUser(auth.user);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const acceptAuth = useCallback((auth) => {
    storeTokens(auth);
    setUser(auth.user);
    return auth.user;
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    // A failed revoke must not trap the user inside a signed-in shell.
    if (refreshToken) await api.logout(refreshToken).catch(() => {});
    signOutLocally();
  }, [signOutLocally]);

  const refreshProfile = useCallback(async () => {
    const profile = await api.me();
    setUser(profile);
    return profile;
  }, []);

  const value = useMemo(
    () => ({ user, initializing, acceptAuth, logout, refreshProfile, setUser }),
    [user, initializing, acceptAuth, logout, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
